import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '../../hooks/useQuery';
import Loading from '../../common/Loading';
import StatusPreview from './StatusPreview';
import { decode } from '../../lib/decodeToken';
import { useMutation } from '../../hooks/useMutation';
import { EXTEND_DWELLING } from '../table';
import { toastr } from 'app/common/toastr';

const DWELLING = `
query Dwelling($id: ID!, $userId: ID) {
  dwelling(id: $id, userId: $userId) {
    id
    headline
    location {
      id
      href
      address
      street
      house
      floor
      door
      zip
      city
      geolocation {
        lat
        lng
      }
    }
    listingType
    payment {
      transaction
      product
      currency
      amount
      captured
      voided
    }
    status
    active
  }
}
`;

const TOGGLE_ACTIVE = `
mutation ToggleActive($dwellingId: ID!, $active: Boolean!) {
  toggleActive(id: $dwellingId, active: $active)
}
`;

const safeDecode = (tokenString) => {
  if (!tokenString) {
    return null;
  }

  try {
    return decode(tokenString);
  } catch (err) {
    return null;
  }
};

const StatusView = () => {
  const params = useParams();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const tokenString = searchParams.get('token');
  const token = safeDecode(tokenString);

  const dwellingId = (params && params.uuid) || (token && token.dwellingId);
  const userId = (token && token.id) || null;

  if (!dwellingId) {
    return 'Vi beklager meget. Enten mangler der en token eller også er den udløbet';
  }

  const { data, loading, errors } = useQuery(DWELLING, {
    variables: {
      id: dwellingId,
      userId
    }
  });

  const mutationOptions = { token: tokenString };

  const { mutate: extendDwelling } = useMutation(
    EXTEND_DWELLING,
    mutationOptions
  );
  const { mutate: toggleActive } = useMutation(TOGGLE_ACTIVE, mutationOptions);

  const [dwelling, setDwelling] = React.useState(null);

  React.useEffect(() => {
    if (data && data.dwelling) {
      setDwelling(data.dwelling);
    }
  }, [data]);

  const onToggleActive = async () => {
    if (!dwelling) {
      return;
    }

    const res = await toggleActive({
      dwellingId: dwelling.id,
      active: !dwelling.active
    });

    if (res && res.data) {
      setDwelling((prev) => ({ ...prev, active: res.data.toggleActive }));
    }
  };

  const onExtendDwelling = async () => {
    if (!dwelling) {
      return;
    }

    const res = await extendDwelling({ dwellingId: dwelling.id });

    if (res && res.data && res.data.extendDwelling !== null) {
      return toastr.success(
        'Vi har noteret, at dit lejemål stadig er ledigt og er forlænget med 90 dage'
      );
    }

    return toastr.error(
      'Vi beklager meget, der er sket en fejl. Prøv at genindlæse siden eller kontakt kundeservice'
    );
  };

  if (loading || !dwelling) {
    return <Loading />;
  }

  if (errors) {
    return 'Vi beklager meget, der er sket en fejl. Prøv at genindlæse siden eller kontakt kundeservice';
  }

  const approved = dwelling.status === 'APPROVED';

  return (
    <StatusPreview
      dwelling={dwelling}
      setDwelling={setDwelling}
      approved={approved}
      token={token}
      mutationOptions={mutationOptions}
      onExtend={onExtendDwelling}
      onToggleActive={onToggleActive}
    />
  );
};

export default StatusView;
